import { PageLayout, ContentContainer, PageTitle } from "components/layout";
import { X } from "lucide-react";
import { useSpedDevolutionState } from "../ducks/hooks/state";
import { useSpedDevolutionActions } from "../ducks/hooks/actions";
import { useUploadSpedDevolutionFilesMutation } from "@/services/tax/sped.devolution";
import { useState } from "react";
import { Button } from "components/ui/button";
import { Card, CardContent } from "components/ui/card";
import { Input } from "components/ui/input";
import { toast } from "components/ui/use-toast";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "components/ui/tooltip";
import { downloadFromBlob } from "@/utils/download-blob";

export const SpedDevolutionList: React.FC = () => {
  const { singleFile } = useSpedDevolutionState();
  const [processedBlob, setProcessedBlob] = useState<Blob | null>(null);
  const { updateSingleFile, clearAllFiles } = useSpedDevolutionActions();
  const [uploadSpedDevolutionFiles, { isLoading }] =
    useUploadSpedDevolutionFilesMutation();
  const [resetInput, setResetInput] = useState(0);

  const handleSingleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setProcessedBlob(null);
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.type !== "text/plain") {
        toast({
          variant: "destructive",
          description: "O arquivo único deve ser um arquivo .txt",
          duration: 5000,
        });
      } else {
        updateSingleFile(file);
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!singleFile) {
      toast({
        variant: "destructive",
        description: "Por favor, selecione um arquivo SPED.",
        duration: 5000,
      });
      return;
    }

    const formData = new FormData();
    formData.append("files", singleFile);

    try {
      const blob = await uploadSpedDevolutionFiles({ body: formData }).unwrap();
      setProcessedBlob(blob);

      downloadFromBlob(blob, "sped_devolucao.zip");

      toast({
        variant: "default",
        description: "Arquivo ZIP gerado com sucesso.",
        duration: 5000,
      });
    } catch (err) {
      toast({
        variant: "destructive",
        description: "Erro ao processar e baixar o arquivo ZIP.",
        duration: 5000,
      });
    } finally {
      clearAllFiles();
      updateSingleFile(null);
      setResetInput((prev) => prev + 1);
    }
  };

  const getDownloadLink = () => {
    if (!processedBlob) return null;
    return URL.createObjectURL(processedBlob);
  };

  const downloadLink = getDownloadLink();

  const handleRemoveSingleFile = () => {
    updateSingleFile(null);
    setResetInput((prev) => prev + 1);
  };

  return (
    <PageLayout>
      <PageTitle
        title="Sped Devolução"
        description="Geração do arquivo Sped de devolução"
      />
      <ContentContainer>
        <div className="flex flex-col items-start justify-start w-full relative">
          <div className="w-full max-w-[40%] mr-auto">
            <form
              onSubmit={handleSubmit}
              className="space-y-4 w-full bg-white rounded-lg"
            >
              <div className="flex flex-col space-y-2">
                <span className="text-sm text-gray-500">
                  Selecione o SPED de devolução original para processamento.
                </span>
                <div className="flex items-center justify-center h-12 border rounded-lg bg-white">

                  <label
                    htmlFor="single-file"
                    className="w-full h-full flex items-center justify-center cursor-pointer text-gray-500"
                  >
                    Selecione o arquivo
                  </label>
                  <Input
                    id="single-file"
                    type="file"
                    accept=".txt"
                    disabled={isLoading}
                    onChange={handleSingleFileChange}
                    className="hidden"
                    key={`single-${resetInput}`}
                  />
                </div>

                {singleFile && (
                  <Card>
                    <CardContent className="flex items-center justify-between p-4">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-sm truncate max-w-[85%] cursor-default">
                              {singleFile?.name}
                            </span>
                          </TooltipTrigger>
                          <TooltipContent>{singleFile?.name}</TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                      <X
                        onClick={handleRemoveSingleFile}
                        className="h-4 w-4 cursor-pointer text-gray-500 hover:text-red-600"
                      />
                    </CardContent>
                  </Card>
                )}
              </div>

              <div className="flex space-x-2">

                <Button
                  type="submit"
                  isLoading={isLoading}
                  className="w-auto h-auto text-lg rounded-md w-full"
                >
                  Processar arquivo
                </Button>
                {processedBlob && (
                  <a
                    href={downloadLink || undefined}
                    download="sped_atualizado.zip"
                  >
                    <Button type="button" className="h-12 text-lg">
                      Baixar
                    </Button>
                  </a>
                )}
                
              </div>
            </form>
          </div>
        </div>
      </ContentContainer>
    </PageLayout>
  );
};
